import { Component, inject } from '@angular/core';
import { ComplianceOverviewRequirement, ComplianceOverviewRequirementsApiRequest, ComplianceOverviewRequirementsRequest } from '../../models/compliance-item.model';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ComplianceStatusIndicatorComponent } from '../../../_shared/components/compliance-status-indicator/compliance-status-indicator.component';
import { ComplianceOverviewService } from '../../services/compliance-overview.service';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { Subject, Subscription } from 'rxjs';
import { ComplianceStateService } from '../../../_shared/services/compliance-state.service';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { TranslocoPipe } from '@jsverse/transloco';

interface PaginationState {
  page: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
}

const DEFAULT_PAGE_SIZE = 10;

@Component({
  selector: 'app-compliance-overview-details',
  standalone: true,
  imports: [
    CommonModule,
    ComplianceStatusIndicatorComponent,
    EmptyStateComponent,
    InfiniteScrollModule,
    TranslocoPipe
  ],
  templateUrl: './compliance-overview-details.component.html',
  styleUrl: './compliance-overview-details.component.scss'
})
export class ComplianceOverviewDetailsComponent {
  private complianceOverviewService: ComplianceOverviewService = inject(ComplianceOverviewService);
  private complianceOverviewStateService: ComplianceStateService = inject(ComplianceStateService);
  private errorService: ErrorHandlerService = inject(ErrorHandlerService);
  private router: Router = inject(Router);
  private readonly cancelSubject = new Subject<void>();

  private requestSubscription?: Subscription;
  complianceRequirementItems: ComplianceOverviewRequirement[] = [];
  isLoading: boolean = true;
  isLoadingMore: boolean = false;
  pagination: PaginationState = { page: 1, pageSize: DEFAULT_PAGE_SIZE, totalPages: 0, totalCount: 0 };

  ngOnInit() {
    this.requestSubscription = this.complianceOverviewStateService.request$.subscribe(() => {
      this.pagination = { ...this.pagination, page: 1, totalPages: 0, totalCount: 0 };
      this.complianceRequirementItems = [];
      void this.getCompliancesRequirementsAsync(true);
    });
  }

  ngOnDestroy() {
    if (this.requestSubscription) {
      this.requestSubscription.unsubscribe();
    }
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  get hasMoreResults(): boolean {
    return this.pagination.page < this.pagination.totalPages;
  }

  async getCompliancesRequirementsAsync(resetResults: boolean = false): Promise<void> {
    const requestedPage = this.pagination.page;

    try {
      if (requestedPage === 1 || resetResults) {
        this.isLoading = true;
        this.isLoadingMore = false;
        this.cancelSubject.next();
      } else {
        this.isLoadingMore = true;
      }

      const apiRequest = this.buildApiRequest(requestedPage);
      const results = await this.complianceOverviewService.getCompliancesRequirementsAsync(
        apiRequest,
        this.cancelSubject
      );
      const data = results.data;
      const complianceRequirements = data?.results ?? [];

      this.complianceRequirementItems = requestedPage === 1
        ? complianceRequirements
        : [...this.complianceRequirementItems, ...complianceRequirements];

      this.pagination = {
        ...this.pagination,
        totalCount: data?.totalCount ?? 0,
        totalPages: data?.totalPages ?? 0
      };
      this.isLoading = false;
      this.isLoadingMore = false;
    } catch (error) {
      if (requestedPage > 1) {
        this.pagination = { ...this.pagination, page: requestedPage - 1 };
      }

      this.errorService.handleError(error, {
        callback: () => {
          this.isLoading = false;
          this.isLoadingMore = false;
        }
      });
    }
  }

  viewComplianceAssets(complianceRequirement: ComplianceOverviewRequirement): void {
    this.router.navigate(['/asset-management/compliance-overview/compliance-asset-list'], {
      queryParams: {
        requirementId: complianceRequirement.requirementId,
        requirementName: complianceRequirement.requirementName,
        complianceId: complianceRequirement.complianceId,
        complianceName: complianceRequirement.complianceName
      }
    });
  }

  onScrollDown(): void {
    if (this.isLoading || this.isLoadingMore || !this.hasMoreResults) {
      return;
    }

    this.pagination = { ...this.pagination, page: this.pagination.page + 1 };
    void this.getCompliancesRequirementsAsync();
  }

  private buildApiRequest(page: number): ComplianceOverviewRequirementsApiRequest {
    const filters = this.complianceOverviewStateService.getCurrentRequest();
    const region = filters?.complianceRegion;
    return {
      accountIds: filters?.accountIds ?? [],
      page,
      pageSize: this.pagination.pageSize,
      searchTerm: filters?.searchTerm ?? null,
      complianceId: filters?.complianceId ?? null,
      regionId: region?.type === 'region' ? region.id : null,
      countryId: region?.type === 'country' ? region.id : null
    };
  }
}